<div class="kyc-page min-h-screen py-8 px-4">
  <div class="kyc-card max-w-4xl mx-auto">

    <!-- Header -->
    <div class="kyc-head">
      <button (click)="CloseModal()" class="kyc-icon-btn" aria-label="Back">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>

      <div class="text-center">
        <h1 class="kyc-title">KYC Verification</h1>
        <p class="kyc-subtitle">Complete your identity verification to unlock full access</p>
      </div>

      <div *ngIf="isPending === 'Pending'" class="kyc-pending-pill">
        <span class="kyc-pending-dot"></span>
        In Review
      </div>
    </div>

    <form [formGroup]="kycForm" (ngSubmit)="onSubmit()" class="kyc-body">

      <!-- Section 1: Personal Information -->
      <div class="kyc-section">
        <div class="kyc-section-head">
          <span class="kyc-section-badge">1</span>
          Personal Information
          <span class="kyc-section-rule"></span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- First Name -->
          <div class="kyc-field">
            <label class="kyc-label">First Name <span class="kyc-req">*</span></label>
            <input type="text" formControlName="firstName" placeholder="Enter first name"
              [class.kyc-error]="isFormSubmitted && f['firstName'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['firstName'].hasError('required')" class="kyc-error-text">
              First name is required
            </span>
            <span *ngIf="isFormSubmitted && f['firstName'].hasError('minlength')" class="kyc-error-text">
              Minimum 2 characters required
            </span>
          </div>

          <!-- Middle Name -->
          <div class="kyc-field">
            <label class="kyc-label">Middle Name</label>
            <input type="text" formControlName="middleName" placeholder="Enter middle name (optional)"
              class="kyc-input" />
          </div>

          <!-- Last Name -->
          <div class="kyc-field">
            <label class="kyc-label">Last Name <span class="kyc-req">*</span></label>
            <input type="text" formControlName="lastName" placeholder="Enter last name"
              [class.kyc-error]="isFormSubmitted && f['lastName'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['lastName'].hasError('required')" class="kyc-error-text">
              Last name is required
            </span>
          </div>

          <!-- Date of Birth -->
          <div class="kyc-field">
            <label class="kyc-label">Date of Birth <span class="kyc-req">*</span></label>
            <input type="date" formControlName="dateOfBirth" [min]="minDate" [max]="maxDate"
              [class.kyc-error]="isFormSubmitted && f['dateOfBirth'].invalid" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['dateOfBirth'].hasError('required')" class="kyc-error-text">
              Date of birth is required
            </span>
            <span *ngIf="isFormSubmitted && f['dateOfBirth'].hasError('underage')" class="kyc-error-text">
              You must be at least 18 years old
            </span>
          </div>

          <!-- Nationality -->
          <div class="kyc-field">
            <label class="kyc-label">Nationality <span class="kyc-req">*</span></label>
            <input type="text" formControlName="nationality" placeholder="Enter nationality"
              [class.kyc-error]="isFormSubmitted && f['nationality'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['nationality'].hasError('required')" class="kyc-error-text">
              Nationality is required
            </span>
          </div>
        </div>
      </div>

      <!-- Section 2: Address Information -->
      <div class="kyc-section">
        <div class="kyc-section-head">
          <span class="kyc-section-badge">2</span>
          Address Information
          <span class="kyc-section-rule"></span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Country -->
          <div class="kyc-field">
            <label class="kyc-label">Country <span class="kyc-req">*</span></label>
            <input type="text" formControlName="country" placeholder="Enter country"
              [class.kyc-error]="isFormSubmitted && f['country'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['country'].hasError('required')" class="kyc-error-text">
              Country is required
            </span>
          </div>

          <!-- State -->
          <div class="kyc-field">
            <label class="kyc-label">State/Province <span class="kyc-req">*</span></label>
            <input type="text" formControlName="state" placeholder="Enter state"
              [class.kyc-error]="isFormSubmitted && f['state'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['state'].hasError('required')" class="kyc-error-text">
              State is required
            </span>
          </div>

          <!-- City -->
          <div class="kyc-field">
            <label class="kyc-label">City <span class="kyc-req">*</span></label>
            <input type="text" formControlName="city" placeholder="Enter city"
              [class.kyc-error]="isFormSubmitted && f['city'].hasError('required')" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['city'].hasError('required')" class="kyc-error-text">
              City is required
            </span>
          </div>

          <!-- ZIP Code -->
          <div class="kyc-field">
            <label class="kyc-label">ZIP Code <span class="kyc-req">*</span></label>
            <input type="text" formControlName="zipcode" placeholder="Enter ZIP code"
              [class.kyc-error]="isFormSubmitted && f['zipcode'].invalid" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['zipcode'].hasError('required')" class="kyc-error-text">
              ZIP code is required
            </span>
            <span *ngIf="isFormSubmitted && f['zipcode'].hasError('pattern')" class="kyc-error-text">
              ZIP code must be 4-10 digits
            </span>
          </div>

          <!-- Address -->
          <div class="kyc-field md:col-span-2">
            <label class="kyc-label">Address <span class="kyc-req">*</span></label>
            <textarea formControlName="address" placeholder="Enter street address" rows="2"
              [class.kyc-error]="isFormSubmitted && f['address'].hasError('required')"
              class="kyc-input resize-none"></textarea>
            <span *ngIf="isFormSubmitted && f['address'].hasError('required')" class="kyc-error-text">
              Address is required
            </span>
          </div>
        </div>
      </div>

      <!-- Section 3: Document Information -->
      <div class="kyc-section">
        <div class="kyc-section-head">
          <span class="kyc-section-badge">3</span>
          Document Information
          <span class="kyc-section-rule"></span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- SSIN -->
          <div class="kyc-field">
            <label class="kyc-label">SSIN <span class="kyc-req">*</span></label>
            <input type="text" formControlName="ssin" placeholder="Enter SSIN"
              [class.kyc-error]="isFormSubmitted && f['ssin'].invalid" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['ssin'].hasError('required')" class="kyc-error-text">
              SSIN is required
            </span>
            <span *ngIf="isFormSubmitted && f['ssin'].hasError('pattern')" class="kyc-error-text">
              SSIN must be 9-15 digits
            </span>
          </div>

          <!-- Document Type -->
          <div class="kyc-field">
            <label class="kyc-label">Document Type <span class="kyc-req">*</span></label>
            <select formControlName="documentType"
              [class.kyc-error]="isFormSubmitted && f['documentType'].hasError('required')" class="kyc-select">
              <option value="" disabled>Select document type</option>
              <option *ngFor="let type of documentTypes" [value]="type.value">{{ type.label }}</option>
            </select>
            <span *ngIf="isFormSubmitted && f['documentType'].hasError('required')" class="kyc-error-text">
              Document type is required
            </span>
          </div>

          <!-- Document Expiry Date -->
          <div class="kyc-field">
            <label class="kyc-label">Document Expiry Date <span class="kyc-req">*</span></label>
            <input type="date" formControlName="documentExpiryDate"
              [class.kyc-error]="isFormSubmitted && f['documentExpiryDate'].invalid" class="kyc-input" />
            <span *ngIf="isFormSubmitted && f['documentExpiryDate'].hasError('required')" class="kyc-error-text">
              Expiry date is required
            </span>
            <span *ngIf="isFormSubmitted && f['documentExpiryDate'].hasError('expired')" class="kyc-error-text">
              Document is expired
            </span>
          </div>
        </div>
      </div>

      <!-- Section 4: Document Images -->
      <div class="kyc-section">
        <div class="kyc-section-head">
          <span class="kyc-section-badge">4</span>
          Document Images
          <span class="kyc-section-rule"></span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Document Front Image -->
          <div class="kyc-field">
            <label class="kyc-label">Document Front <span class="kyc-req">*</span></label>
            <div class="space-y-3">
              <input type="file" #documentFrontInput accept="image/*" (change)="onDocumentFrontImageSelected($event)"
                hidden />
              <button type="button" (click)="documentFrontInput.click()" class="kyc-upload-btn">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Upload Front Image
              </button>
              <div *ngIf="documentFrontImagePreview" class="kyc-preview">
                <img [src]="documentFrontImagePreview" alt="Document Front Preview" class="kyc-preview-img" />
                <button type="button" (click)="removeDocumentFrontImage()" class="kyc-remove-btn">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <span *ngIf="isFormSubmitted && f['documentFrontImage'].hasError('required')" class="kyc-error-text">
                Document front image is required
              </span>
            </div>
          </div>

          <!-- Document Back Image -->
          <div class="kyc-field">
            <label class="kyc-label">Document Back <span class="kyc-req">*</span></label>
            <div class="space-y-3">
              <input type="file" #documentBackInput accept="image/*" (change)="onDocumentBackImageSelected($event)"
                hidden />
              <button type="button" (click)="documentBackInput.click()" class="kyc-upload-btn">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Upload Back Image
              </button>
              <div *ngIf="documentBackImagePreview" class="kyc-preview">
                <img [src]="documentBackImagePreview" alt="Document Back Preview" class="kyc-preview-img" />
                <button type="button" (click)="removeDocumentBackImage()" class="kyc-remove-btn">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <span *ngIf="isFormSubmitted && f['documentBackImage'].hasError('required')" class="kyc-error-text">
                Document back image is required
              </span>
            </div>
          </div>

          <!-- Selfie Image -->
          <div class="kyc-field">
            <label class="kyc-label">Selfie <span class="kyc-req">*</span></label>
            <div class="space-y-3">
              <input type="file" #selfieInput accept="image/*" (change)="onSelfieImageSelected($event)" hidden />
              <button type="button" (click)="selfieInput.click()" class="kyc-upload-btn">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Upload Selfie
              </button>
              <div *ngIf="selfieImagePreview" class="kyc-preview">
                <img [src]="selfieImagePreview" alt="Selfie Preview" class="kyc-preview-img" />
                <button type="button" (click)="removeSelfieImage()" class="kyc-remove-btn">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <span *ngIf="isFormSubmitted && f['selfieImage'].hasError('required')" class="kyc-error-text">
                Selfie is required
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Buttons -->
      <div class="flex gap-3 justify-end">
        <button type="button" (click)="CloseModal()" class="kyc-btn-ghost">
          Cancel
        </button>
        <button type="submit" [disabled]="isPending === 'Pending'" class="kyc-btn-gold">
          Submit
        </button>
      </div>
    </form>
  </div>
</div>

<!-- KYC Popup -->
<app-kyc-popup *ngIf="showKycPopup" [title]="popupTitle" [message]="popupMessage" [buttonText]="popupButtonText"
  [redirectRoute]="popupRedirect" (close)="closeKycPopup()">
</app-kyc-popup>
